import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from './ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const EditBookmarkDialog = ({ bookmark, categories, statuses, tags = [], open, onOpenChange, onSave, onClose }) => {
  // Compatibility: support both new (open/onOpenChange) and old (onClose) props
  const isOpen = open !== undefined ? open : true;
  const handleOpenChange = (newOpen) => {
    if (onOpenChange) {
      onOpenChange(newOpen);
    } else if (onClose && !newOpen) {
      onClose();
    }
  };
  const handleClose = () => handleOpenChange(false);
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    title: '',
    url: '',
    notes: '',
    category_id: null,
    status: 'active',
    tags: []
  });
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);

  useEffect(() => {
    if (bookmark) {
      setFormData({
        title: bookmark.title || '',
        url: bookmark.url || '',
        notes: bookmark.notes || '',
        category_id: bookmark.category_id || null,
        status: bookmark.status || 'active',
        tags: bookmark.tags || []
      });
    }
  }, [bookmark]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const url = bookmark
        ? `${API_URL}/api/bookmarks/${bookmark.bookmark_id}`
        : `${API_URL}/api/bookmarks`;
      const method = bookmark ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(t('bookmark_saved'));
        handleClose();
        onSave();
      } else {
        throw new Error('Failed to save bookmark');
      }
    } catch (error) {
      console.error('Failed to save bookmark:', error);
      toast.error(t('error_occurred'));
    } finally {
      setSaving(false);
    }
  };

  const addTag = (tagName = tagInput) => {
    const normalizedTag = tagName.trim().toLowerCase();
    if (normalizedTag && !formData.tags.includes(normalizedTag)) {
      setFormData({ ...formData, tags: [...formData.tags, normalizedTag] });
      setTagInput('');
      setShowTagSuggestions(false);
    }
  };

  const removeTag = (tag) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
  };

  const filteredSuggestions = tags.filter(
    tag => tag.name.includes(tagInput.toLowerCase()) && !formData.tags.includes(tag.name)
  );

  const getTagColor = (tagName) => {
    const tag = tags.find(t => t.name === tagName);
    return tag?.color || '#0891b2';
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-bookmark-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            {bookmark ? t('edit_bookmark') : t('add_bookmark')}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">{t('bookmark_title')} *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring bg-background"
              required
              data-testid="bookmark-title-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">{t('bookmark_url')} *</label>
            <input
              type="url"
              value={formData.url}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring bg-background"
              required
              data-testid="bookmark-url-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">{t('bookmark_notes')}</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring bg-background"
              rows={3}
              data-testid="bookmark-notes-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">{t('bookmark_category')}</label>
            <select
              value={formData.category_id || ''}
              onChange={(e) => setFormData({ ...formData, category_id: e.target.value || null })}
              className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring bg-background"
              data-testid="bookmark-category-select"
            >
              <option value="">{t('no_category')}</option>
              {categories.map(cat => (
                <option key={cat.category_id} value={cat.category_id}>
                  {'  '.repeat(cat.path.length - 1)}{cat.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">{t('bookmark_status')}</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring bg-background"
              data-testid="bookmark-status-select"
            >
              {statuses.map(status => (
                <option key={status.status_id} value={status.status_id}>
                  {status.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">{t('tags')}</label>
            <div className="relative">
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => {
                    setTagInput(e.target.value);
                    setShowTagSuggestions(true);
                  }}
                  onFocus={() => setShowTagSuggestions(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                  className="flex-1 px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring bg-background"
                  placeholder={t('tag_name')}
                  data-testid="tag-input"
                />
                <button
                  type="button"
                  onClick={() => addTag()}
                  className="px-4 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/80"
                >
                  +
                </button>
              </div>
              
              {/* Tag Suggestions */}
              {showTagSuggestions && tagInput && filteredSuggestions.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-40 overflow-y-auto">
                  {filteredSuggestions.map(tag => (
                    <button
                      key={tag.tag_id}
                      type="button"
                      onClick={() => addTag(tag.name)}
                      className="w-full px-4 py-2 text-left hover:bg-accent/10 flex items-center gap-2 text-sm"
                    >
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: tag.color }} />
                      {tag.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Selected Tags */}
            <div className="flex flex-wrap gap-2 mt-2">
              {formData.tags.map(tag => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm"
                  style={{ 
                    backgroundColor: getTagColor(tag) + '20',
                    color: getTagColor(tag)
                  }}
                >
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: getTagColor(tag) }} />
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="hover:opacity-70 rounded-full"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              ))}
            </div>
          </div>

          <DialogFooter className="flex gap-3 pt-4 border-t border-border">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 border border-input py-2.5 rounded-lg font-medium hover:bg-accent/10"
              data-testid="cancel-bookmark-button"
            >
              {t('cancel')}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 bg-primary text-primary-foreground py-2.5 rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50"
              data-testid="save-bookmark-button"
            >
              {saving ? t('loading') : t('save')}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditBookmarkDialog;
